// ============= Imports =============
import React from 'react';
import { Button } from '@/ui';
import styled from 'styled-components';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import type { VisualPromptReviewContext } from './types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const Note = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors?.text?.secondary || '#666'};
  line-height: 1.45;
`;

const ContextList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
`;

const ContextItem = styled.li`
  color: ${({ theme }) => theme.colors?.text?.primary || '#1f2937'};

  strong {
    color: ${({ theme }) => theme.colors?.text?.secondary || '#666'};
    font-weight: 600;
    margin-right: 0.35rem;
  }
`;

const Warning = styled.p`
  margin: 0;
  padding: 0.65rem 0.75rem;
  font-size: 0.82rem;
  line-height: 1.4;
  border-radius: ${({ theme }) => theme.borderRadius?.sm || '6px'};
  background: #fff8e6;
  border: 1px solid #fcd34d;
  color: #92400e;
`;

const PreviewBox = styled.pre`
  white-space: pre-wrap;
  background: ${({ theme }) => theme.colors?.background?.secondary || '#f5f5f5'};
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  max-height: 280px;
  overflow: auto;
  margin: 0;
`;

const SaveRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

export interface VisualPromptReviewStepProps {
  compiledPreview: string;
  reviewContext?: VisualPromptReviewContext;
  requiresContent?: boolean;
  previewIsThin?: boolean;
  onSaveChoices?: () => void;
  saveChoicesDisabled?: boolean;
}

// ============= Component =============
export const VisualPromptReviewStep: React.FC<VisualPromptReviewStepProps> = ({
  compiledPreview,
  reviewContext,
  requiresContent,
  previewIsThin,
  onSaveChoices,
  saveChoicesDisabled,
}) => (
  <Container>
    <Title>Review your prompt</Title>
    <Note>{VISUAL_PROMPT_MESSAGES.oneTimeUseNote}</Note>
    <Note>{VISUAL_PROMPT_MESSAGES.attachAfterRunHint}</Note>

    {reviewContext &&
      (reviewContext.workspaceName ||
        reviewContext.channel ||
        reviewContext.matterRelated) && (
        <ContextList>
          {reviewContext.workspaceName && (
            <ContextItem>
              <strong>{VISUAL_PROMPT_MESSAGES.reviewWorkspace}:</strong>
              {reviewContext.workspaceName}
            </ContextItem>
          )}
          {reviewContext.channel && (
            <ContextItem>
              <strong>{VISUAL_PROMPT_MESSAGES.reviewChannel}:</strong>
              {reviewContext.channel}
            </ContextItem>
          )}
          {reviewContext.matterRelated && (
            <ContextItem>
              <strong>{VISUAL_PROMPT_MESSAGES.reviewMatter}:</strong>
              {reviewContext.matterRelated}
            </ContextItem>
          )}
        </ContextList>
      )}

    {previewIsThin && requiresContent && (
      <Warning>{VISUAL_PROMPT_MESSAGES.previewThinWarning}</Warning>
    )}

    <strong>{VISUAL_PROMPT_MESSAGES.previewTitle}</strong>
    <PreviewBox>{compiledPreview || '(empty)'}</PreviewBox>

    {onSaveChoices && (
      <SaveRow>
        <Button
          type="button"
          variant="secondary"
          onClick={onSaveChoices}
          disabled={saveChoicesDisabled}
        >
          {VISUAL_PROMPT_MESSAGES.saveChoices}
        </Button>
      </SaveRow>
    )}
  </Container>
);
